import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import type { IFarm } from 'interfaces/IFarm';
import { ZoneLinkType, type IUnit } from 'interfaces/IInput';
import type { IPaddock } from 'interfaces/IPaddock';
import type { PartialUserProfile } from 'interfaces/IUserProfile';
import type { IZone } from 'interfaces/IZone';
import { v4 as uuidv4 } from 'uuid';
import { getUserDisplayName } from 'utils/UserUtils';

// Builders for the Farm/Upsert and Paddock/Upsert payloads (OP-2481). They
// mirror the mobile app's buildFarm / buildPaddock / buildZone factories, so a
// farm or paddock saved from the web is the same document mobile would write.

/** Zone type bitmask flags, as defined by the mobile app's ZoneType enum. */
const ZONE_TYPE_DEFAULT = 1;
const ZONE_TYPE_CROPPING = 2;
/** A new paddock's default zone is named like mobile's. */
const DEFAULT_ZONE_NAME = 'Zone 1';

export interface FarmFormValues {
  farmName: string;
  farmArea: number | null;
  areaUnit: IUnit | null;
  notes: string | null;
}

export interface PaddockFormValues {
  paddockName: string;
  paddockArea: number | null;
  /** Null when not set — the default zone then takes the paddock area. */
  arableArea: number | null;
  areaUnit: IUnit | null;
  notes: string | null;
}

const blankToNull = (value: string | null): string | null =>
  value && value.trim() ? value.trim() : null;

/** The modifier fields every update stamps, as mobile's buildUpdateBase. */
const buildModifierStamp = (
  userProfile: PartialUserProfile | undefined,
  now: string
) => ({
  dateModified: now,
  modifierId: userProfile?.id ?? '',
  snapModifier:
    getUserDisplayName(userProfile?.firstName, userProfile?.lastName) || '',
  sourceId: UNIQUE_DEVICE_ID,
});

/** The base fields of a new document, as mobile's buildBase. */
const buildCreateStamp = (
  orgId: string,
  userProfile: PartialUserProfile | undefined,
  now: string
) => {
  const modifier = buildModifierStamp(userProfile, now);
  return {
    id: uuidv4(),
    version: 1,
    dateCreated: now,
    creatorId: modifier.modifierId,
    snapCreator: modifier.snapModifier,
    ...modifier,
    isActive: true,
    orgId,
  };
};

/** The default zone's area: the arable area when set, else the paddock area. */
const resolveZoneArea = (values: PaddockFormValues): number =>
  values.arableArea || values.paddockArea || 0;

export const buildNewFarm = (
  orgId: string,
  values: FarmFormValues,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): IFarm => ({
  ...buildCreateStamp(orgId, userProfile, now),
  farmName: values.farmName.trim(),
  farmArea: values.farmArea ?? 0,
  areaUnit: values.areaUnit,
  isExcluded: false,
  notes: blankToNull(values.notes),
  propertyIdCodes: null,
  paddocks: [],
});

export const buildFarmUpdate = (
  farm: IFarm,
  values: FarmFormValues,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): IFarm => ({
  ...farm,
  ...buildModifierStamp(userProfile, now),
  farmName: values.farmName.trim(),
  farmArea: values.farmArea ?? 0,
  areaUnit: values.areaUnit,
  notes: blankToNull(values.notes),
});

/**
 * Excludes or re-includes a farm. Its active paddocks follow in the same
 * save: the backend doesn't cascade, so the payload carries it (OP-2481).
 */
export const buildFarmExcluded = (
  farm: IFarm,
  isExcluded: boolean,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): IFarm => ({
  ...farm,
  ...buildModifierStamp(userProfile, now),
  isExcluded,
  paddocks: farm.paddocks.map((paddock) =>
    paddock.isActive ? { ...paddock, isExcluded } : paddock
  ),
});

/**
 * A new paddock and its default zone, saved together by Paddock/Upsert. The
 * zone links back to the paddock so the map and new plans can find it.
 */
export const buildNewPaddock = (
  orgId: string,
  values: PaddockFormValues,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): { paddock: IPaddock; zone: IZone } => {
  const paddockId = uuidv4();
  const paddockName = values.paddockName.trim();

  const zone: IZone = {
    ...buildCreateStamp(orgId, userProfile, now),
    zoneType: ZONE_TYPE_DEFAULT + ZONE_TYPE_CROPPING,
    isExcluded: false,
    zoneName: DEFAULT_ZONE_NAME,
    zoneArea: resolveZoneArea(values),
    areaUnit: values.areaUnit,
    geoData: null,
    links: [
      {
        linkId: paddockId,
        linkType: ZoneLinkType.Paddock,
        snapLinkName: paddockName,
      },
    ],
  };

  const paddock: IPaddock = {
    paddockId,
    paddockName,
    paddockArea: values.paddockArea ?? 0,
    arableArea: values.arableArea,
    areaUnit: values.areaUnit,
    isExcluded: false,
    notes: blankToNull(values.notes),
    zoneId: zone.id,
    isActive: true,
    ancestries: [],
  };

  return { paddock, zone };
};

/**
 * An edited paddock and, when it has one, its zone — the zone's area, unit
 * and paddock link name follow the paddock, as on mobile.
 */
export const buildPaddockUpdate = (
  paddock: IPaddock,
  zone: IZone | null,
  values: PaddockFormValues,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): { paddock: IPaddock; zone: IZone | null } => {
  const paddockName = values.paddockName.trim();

  return {
    paddock: {
      ...paddock,
      paddockName,
      paddockArea: values.paddockArea ?? 0,
      arableArea: values.arableArea,
      areaUnit: values.areaUnit,
      notes: blankToNull(values.notes),
    },
    zone: zone
      ? {
          ...zone,
          ...buildModifierStamp(userProfile, now),
          zoneArea: resolveZoneArea(values),
          areaUnit: values.areaUnit,
          links: zone.links.map((link) =>
            link.linkId === paddock.paddockId
              ? { ...link, snapLinkName: paddockName }
              : link
          ),
        }
      : null,
  };
};

/**
 * Excludes or re-includes some of a farm's paddocks in one Farm/Upsert, so a
 * multi-row selection is one save per farm. The farm itself is left as it is.
 */
export const buildPaddocksExcluded = (
  farm: IFarm,
  paddockIds: ReadonlySet<string>,
  isExcluded: boolean,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): IFarm => ({
  ...farm,
  ...buildModifierStamp(userProfile, now),
  paddocks: farm.paddocks.map((paddock) =>
    paddockIds.has(paddock.paddockId) ? { ...paddock, isExcluded } : paddock
  ),
});

/**
 * One Farm/Upsert for a farm's staged grid edits (OP-2481): its own fields
 * and every edited paddock, with those paddocks' zones carried alongside.
 * A paddock no longer on the farm is skipped; one without a zone sends none.
 */
export const buildFarmSave = (
  farm: IFarm,
  farmPatch: Partial<FarmFormValues> | undefined,
  paddockPatches: ReadonlyMap<string, Partial<PaddockFormValues>>,
  zoneFor: (zoneId: string) => IZone | null,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): { farm: IFarm; zones: IZone[] } => {
  const zones: IZone[] = [];
  const paddocks = farm.paddocks.map((paddock) => {
    const patch = paddockPatches.get(paddock.paddockId);
    if (!patch) return paddock;
    const updated = buildPaddockUpdate(
      paddock,
      zoneFor(paddock.zoneId),
      { ...paddockFormValues(paddock), ...patch },
      userProfile,
      now
    );
    if (updated.zone) zones.push(updated.zone);
    return updated.paddock;
  });
  return {
    farm: buildFarmUpdate(
      { ...farm, paddocks },
      { ...farmFormValues(farm), ...farmPatch },
      userProfile,
      now
    ),
    zones,
  };
};

/** Whether a farm and each of its paddocks are excluded — for undo. */
export interface FarmExclusionState {
  isExcluded: boolean;
  paddocks: ReadonlyMap<string, boolean>;
}

export const readFarmExclusion = (farm: IFarm): FarmExclusionState => ({
  isExcluded: farm.isExcluded,
  paddocks: new Map(farm.paddocks.map((p) => [p.paddockId, p.isExcluded])),
});

/**
 * Puts a farm and its paddocks back to a recorded exclusion state (undo or
 * redo of Exclude / Include). Paddocks added since keep their own state.
 */
export const buildFarmExclusion = (
  farm: IFarm,
  state: FarmExclusionState,
  userProfile: PartialUserProfile | undefined,
  now: string = new Date().toISOString()
): IFarm => ({
  ...farm,
  ...buildModifierStamp(userProfile, now),
  isExcluded: state.isExcluded,
  paddocks: farm.paddocks.map((paddock) => {
    const isExcluded = state.paddocks.get(paddock.paddockId);
    return isExcluded === undefined ? paddock : { ...paddock, isExcluded };
  }),
});

/** A farm's current values, to patch one field of for an inline edit. */
export const farmFormValues = (farm: IFarm): FarmFormValues => ({
  farmName: farm.farmName,
  farmArea: farm.farmArea || null,
  areaUnit: farm.areaUnit,
  notes: farm.notes,
});

/** A paddock's current values, to patch one field of for an inline edit. */
export const paddockFormValues = (paddock: IPaddock): PaddockFormValues => ({
  paddockName: paddock.paddockName,
  paddockArea: paddock.paddockArea || null,
  arableArea: paddock.arableArea || null,
  areaUnit: paddock.areaUnit,
  notes: paddock.notes,
});
